// Define interfaces for error types
interface ErrorWithStatusCode {
  statusCode: number
}

interface ErrorWithStatus {
  status: number
}

interface ErrorWithDataStatusCode {
  data: {
    statusCode: number
  }
}

export async function withTokenRetry<T>(apiCall: () => Promise<T>): Promise<T> {
  const { refreshSessionStatus } = useAuthSession()

  try {
    // First attempt
    return await apiCall()
  }
  catch (error: unknown) {
    // Check if it's an authentication error - handle both types of error responses
    const isAuthError
      = (typeof error === 'object' && error !== null
        && (((error as ErrorWithStatusCode).statusCode === 401
          || (error as ErrorWithStatus).status === 401
          || ((error as ErrorWithDataStatusCode).data && (error as ErrorWithDataStatusCode).data.statusCode === 401))))

    if (isAuthError) {
      // Try to refresh the token
      const refresh = await refreshSessionStatus()

      if (refresh === 'refreshed') {
        // Retry the API call with fresh token
        return await apiCall()
      }
      // Sign out only when Firebase or the server refused the session. A
      // refresh that couldn't reach them (offline at the parade) is no reason
      // to: the caller gets the error and the user stays signed in.
      if (refresh === 'unauthorized') {
        navigateTo('/logout')
        throw new Error('Session expired, please log in again')
      }
      throw new Error('Couldn\'t reach the server. Check your connection and try again.')
    }

    // Not an auth error, rethrow
    throw error
  }
}
